import { supabase } from "../../services/SupabaseService";
import { DEFAULT_SELECT_LIMIT } from "../../utils/constants";
import { wait, reportError } from "../../utils/helpers";
import IDatabaseProvider from "../IDatabaseProvider";

class DatabaseProvider implements IDatabaseProvider {
  public maxAttempts = 5;

  public retryIntervalMs = 1000;

  public async selectFirst(
    table: string,
    columns: string,
    filterColumn: string,
    filterValue: any,
    orderByColumn?: string,
    orderByAsc?: boolean
  ): Promise<null | Record<string, any>> {
    const action = async () => {
      let query = supabase.from(table).select(columns).eq(filterColumn, filterValue);

      if (orderByColumn) {
        query = query.order(orderByColumn, { ascending: orderByAsc });
      }

      const { data, error } = await query.limit(1).maybeSingle();

      if (error) {
        throw new Error(`Error: ${error.code} - ${error.message}`);
      }

      return data;
    };

    const contextData = { table, columns, filterColumn, filterValue, orderByColumn, orderByAsc };
    return this.withRetries(action, contextData, `[selectFirst.${table}] Request failed`);
  }

  public async selectAll(
    table: string,
    columns: string,
    filterColumn: null | string,
    filterValue: null | any,
    orderByColumn?: string,
    orderByAsc?: boolean,
    limit = DEFAULT_SELECT_LIMIT
  ): Promise<Record<string, any>[]> {
    const action = async () => {
      let query = supabase.from(table).select(columns);

      if (filterColumn !== null) {
        query = query.eq(filterColumn, filterValue);
      }

      if (orderByColumn) {
        query = query.order(orderByColumn, { ascending: orderByAsc });
      }

      const { data, error } = await query.limit(limit);

      if (error) {
        throw new Error(`Error: ${error.code} - ${error.message}`);
      }

      return data as Record<string, any>[];
    };

    const contextData = { table, columns, filterColumn, filterValue, orderByColumn, orderByAsc, limit };
    return this.withRetries(action, contextData, `[selectAll.${table}] Request failed`);
  }

  public async insert(table: string, newData: Record<string, any>): Promise<Record<string, any>[]> {
    const action = async () => {
      const { data, error } = await supabase.from(table).insert(newData).select();

      if (error) {
        throw new Error(`Error: ${error.code} - ${error.message}`);
      }

      return data as any[];
    };

    const contextData = { table, newData };
    return this.withRetries(action, contextData, `[insert.${table}] Request failed`);
  }

  public async upsert(table: string, newData: Record<string, any>, onConflict: string): Promise<Record<string, any>[]> {
    const action = async () => {
      const { data, error } = await supabase.from(table).upsert(newData, { onConflict, ignoreDuplicates: true }).select();

      if (error) {
        throw new Error(`Error: ${error.code} - ${error.message}`);
      }

      return data as any[];
    };

    const contextData = { table, newData, onConflict };
    return this.withRetries(action, contextData, `[upsert.${table}] Request failed`);
  }

  public async update(table: string, filterColumn: string, filterValue: any, newData: Record<string, any>): Promise<any[]> {
    const action = async () => {
      const { data, error } = await supabase.from(table).update(newData).eq(filterColumn, filterValue).select();

      if (error) {
        throw new Error(`Error: ${error.code} - ${error.message}`);
      }

      return data as any[];
    };

    const contextData = { table, filterColumn, filterValue, newData };
    return this.withRetries(action, contextData, `[update.${table}] Request failed`);
  }

  protected async attemptAction(
    currentAttempt: number,
    action: CallableFunction,
    contextData: Record<string, any>,
    errorMessage: string
  ): Promise<any> {
    // eslint-disable-next-line no-param-reassign
    currentAttempt += 1;

    try {
      return await action();
    } catch (error: any) {
      const tags = { source: "database", attempt: currentAttempt };
      reportError(`[attempt ${currentAttempt}] ${errorMessage}`, { contextData, error }, tags);

      if (currentAttempt < this.maxAttempts) {
        await wait(this.retryIntervalMs);
        return this.attemptAction(currentAttempt, action, contextData, errorMessage);
      }

      const errorObj = new Error(`[halted] ${errorMessage}`) as any;
      errorObj.originalError = error?.toString();
      throw errorObj;
    }
  }

  private async withRetries(action: CallableFunction, contextData: Record<string, any>, errorMessage: string) {
    return this.attemptAction(0, action, contextData, errorMessage);
  }
}

export default DatabaseProvider;
